import React, { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, Eye, Clock, User, X, Share2, ChevronLeft, ChevronRight, Trash2, Video, Megaphone, CheckCircle, Image, ImageOff } from 'lucide-react';
import PageHeader from '../components/ui/PageHeader';
import StatsCard from '../components/ui/StatsCard';
import { useModal } from '../hooks/useModal';
import { useData } from '../contexts/DataContext';
import { Contenuto, StatoContenuto, Cliente } from '../types';
import StatusBadge from '../components/StatusBadge';
import DriveFlagBadge from '../components/DriveFlagBadge';
import OpusBadge from '../components/OpusBadge';
import InstagramPreview from '../components/InstagramPreview';
import { usePageState } from '../hooks/usePageState';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import toast from 'react-hot-toast';
import { formatDateForInput, parseDateFromInput, createDateWithTime, validateDateMonth } from '../utils/dateUtils';
import { generateCalendarDays, generateWeekDays, createDateKey, createAssignmentDate } from '../components/CalendarUtils';
import { isPost, tipoDi, prontoPerProgrammazione, ETICHETTA_TIPO, ETICHETTA_FORMATO, DESCRIZIONE_FORMATO, formatoDi, isCaroselloFoto, etichettaPostCliente } from '../utils/tipoContenuto';

import AccountDestinazioneBadge from '../components/AccountDestinazioneBadge';
import BadgePubblica, { haTagUscita } from '../components/BadgePubblica';
import { suggerisciSlot, giorniUscita, alternaReelEPost, tipoAttesoInData, programmatoOUscito } from '../utils/pianoUscite';

// Il plurale del nome del post del cliente, per le frasi (03/11/2026)
const PLURALE_POST: Record<string, string> = { 'Carosello foto': 'caroselli foto', 'Post grafico': 'post grafici', Post: 'post' };

const Programmazione: React.FC = () => {
  const { state, updateContenuto, updateContenutoStatus, getClienteById, loadContenuti, cancelScheduling, deleteContenuto, toggleVideoRecorded } = useData();
  // SISTEMA MODAL CON useRef per evitare re-render reset
  const modalState = useRef({
    showModal: false,
    selectedContenuto: null as Contenuto | null,
    modalContent: null as 'schedule' | 'assign' | null,
    dataProgrammazione: ''
  });
  
  // Forza re-render quando cambia lo stato del modal
  const [, forceRender] = useState({});
  const triggerRender = () => forceRender({});
  
  // Getter per compatibilità
  const showModal = modalState.current.showModal;
  const selectedContenuto = modalState.current.selectedContenuto;

  // Stato per gestire giorni espansi nel calendario
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());

  // Stato per modal dei contenuti del giorno (mobile-friendly)
  const [dayModal, setDayModal] = useState<{
    isOpen: boolean;
    dayKey: string;
    date: string;
    contenuti: Contenuto[];
  }>({
    isOpen: false,
    dayKey: '',
    date: '',
    contenuti: []
  });

  // Funzioni per gestire l'espansione dei giorni
  const toggleDayExpansion = (dayKey: string) => {
    const newExpandedDays = new Set(expandedDays);
    if (newExpandedDays.has(dayKey)) {
      newExpandedDays.delete(dayKey);
    } else {
      newExpandedDays.add(dayKey);
    }
    setExpandedDays(newExpandedDays);
  };

  const isDayExpanded = (dayKey: string) => expandedDays.has(dayKey);

  // Funzione per aprire il modal del giorno (mobile-friendly)
  const openDayModal = (dayKey: string, date: Date, contenuti: Contenuto[]) => {
    const formattedDate = format(date, 'EEEE d MMMM yyyy', { locale: it });
    setDayModal({
      isOpen: true,
      dayKey,
      date: formattedDate,
      contenuti
    });
  };

  const closeDayModal = () => {
    setDayModal({
      isOpen: false,
      dayKey: '',
      date: '',
      contenuti: []
    });
  };
  const modalContent = modalState.current.modalContent;
  const dataProgrammazione = modalState.current.dataProgrammazione;
  
  // Setter per il modal state
  const setShowModal = (value: boolean) => {
    modalState.current.showModal = value;
    triggerRender();
  };
  
  const setSelectedContenuto = (value: Contenuto | null) => {
    modalState.current.selectedContenuto = value;
    triggerRender();
  };
  
  const setModalContent = (value: 'schedule' | 'assign' | null) => {
    modalState.current.modalContent = value;
    triggerRender();
  };
  
  const setDataProgrammazione = (value: string) => {
    modalState.current.dataProgrammazione = value;
    triggerRender();
  };
  // Vista attiva, mese e filtri stanno nell'URL: il tasto indietro e il refresh
  // riportano alla stessa schermata. Sono anche i parametri del deep-link dalla
  // pagina Videomaker (es. /publisher?view=daProgrammare&client=<id>).
  const [pageState, setPageState] = usePageState(
    {
      view: 'calendario',
      client: '',
      stato: '',
      from: '',
      to: '',
      month: String(new Date().getMonth()),
    },
    { push: ['view'] }
  );
  const activeView = pageState.view as 'calendario' | 'daProgrammare' | 'campagneAdv';
  const setActiveView = (view: 'calendario' | 'daProgrammare' | 'campagneAdv') => setPageState({ view });
  const selectedMonth = Number.isNaN(parseInt(pageState.month, 10))
    ? new Date().getMonth()
    : parseInt(pageState.month, 10);
  const setSelectedMonth = (month: number) => setPageState({ month: String(month) });
  const selectedClienteId = pageState.client;
  const setSelectedClienteId = (client: string) => setPageState({ client });
  const dateFrom = pageState.from;
  const setDateFrom = (from: string) => setPageState({ from });
  const dateTo = pageState.to;
  const setDateTo = (to: string) => setPageState({ to });
  const selectedStato = pageState.stato;
  const setSelectedStato = (stato: string) => setPageState({ stato });

  // Stato per forzare il refresh del calendario
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // Mobile-specific states
  const [isMobile, setIsMobile] = useState(false);
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);
  const [touchEnd, setTouchEnd] = useState<{ x: number; y: number } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [dragStartTime, setDragStartTime] = useState<number>(0);
  const modalRef = useRef<HTMLDivElement>(null);

  const closeModal = () => {
    modalState.current.showModal = false;
    modalState.current.modalContent = null;
    modalState.current.selectedContenuto = null;
    modalState.current.dataProgrammazione = '';
    triggerRender();
  };

  // USEEFFECT RIMOSSO - ERA QUESTO CHE BLOCCAVA LO SCROLL!

  // Escape key handling
  useEffect(() => {
    if (!showModal) return;
    
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeModal();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showModal]);

  // Backdrop click handler
  const handleBackdropClick = (e: React.MouseEvent) => {
    // Solo chiudi se il click è sul backdrop, non sui suoi figli
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Prevent body scroll when main modal is open
  useEffect(() => {
    if (showModal) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
      document.body.style.height = '100vh';
      document.documentElement.style.overflow = 'hidden';

      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.overflow = '';
        document.body.style.height = '';
        document.documentElement.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [showModal]);

  // Prevent body scroll when day modal is open
  useEffect(() => {
    if (dayModal.isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
      document.body.style.height = '100vh';
      document.documentElement.style.overflow = 'hidden';

      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.overflow = '';
        document.body.style.height = '';
        document.documentElement.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [dayModal.isOpen]);

  const monthNames = [
    'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
    'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
  ];

  // Filtra contenuti per la programmazione (testo pronto o montati, non ancora programmati).
  // Le Campagne ADV non sono reel da pubblicare: escluse dalla programmazione.
  // "Da programmare": un contenuto compare qui SOLO dopo che il videomaker lo ha
  // segnato registrato (videoRegistrato) OPPURE se è stato forzato "pronto da
  // programmare" dal pulsante Programma del Videomaker (schedulazione in anticipo
  // di un contenuto non ancora girato). Prima bastava lo stato testo pronto: così
  // comparivano anche script mai girati.
  // I post grafici (solo copy + immagine) non si girano né si montano: entrano
  // qui appena il testo è confermato (vedi prontoPerProgrammazione).
  // Clienti archiviati (status !== 'active'): tutto il loro workflow va escluso da
  // ogni vista e non conteggiato. Filtriamo i contenuti per appartenenza a un
  // cliente attivo.
  const clientiAttiviIds = useMemo(
    () => new Set(state.clienti.filter(c => c.status === 'active').map(c => c.id)),
    [state.clienti]
  );

  const contenutiProntiPerProgrammazione = state.contenuti.filter(
    contenuto => clientiAttiviIds.has(contenuto.clienteId) &&
                (contenuto.stato === StatoContenuto.MONTATO ||
                contenuto.stato === StatoContenuto.TESTO_PRONTO ||
                contenuto.stato === StatoContenuto.REGISTRATO) &&
                !contenuto.campagnaAdv &&
                // "Rimuovi": tolto a mano dalla coda, resta solo nel Copywriter
                !contenuto.rimossoDaProgrammazione &&
                prontoPerProgrammazione(contenuto)
  );

  // Campagne ADV (vista dedicata "solo per noi"): non sono reel da pubblicare.
  const contenutiCampagneAdv = state.contenuti.filter(
    contenuto => clientiAttiviIds.has(contenuto.clienteId) &&
                contenuto.campagnaAdv && contenuto.stato !== StatoContenuto.ARCHIVIATO
  );

  // Contenuti già programmati, pubblicati e archiviati (per il calendario)
  const contenutiProgrammati = state.contenuti.filter(
    contenuto => clientiAttiviIds.has(contenuto.clienteId) && programmatoOUscito(contenuto)
  );

  // Per il calendario editoriale, mostra SEMPRE tutti i contenuti programmati, pubblicati e archiviati
  // Il filtro per mese si applica solo ai contenuti da programmare
  const contenutiCalendario = [
    ...contenutiProntiPerProgrammazione.filter(contenuto => {
      // Filtro cliente
      if (selectedClienteId && contenuto.clienteId !== selectedClienteId) {
        return false;
      }
      // Filtro stato
      if (selectedStato && contenuto.stato !== selectedStato) {
        return false;
      }
      // Filtro date range
      if (dateFrom && new Date(contenuto.createdAt) < new Date(dateFrom)) {
        return false;
      }
      if (dateTo && new Date(contenuto.createdAt) > new Date(dateTo)) {
        return false;
      }
      return true;
    }),
    ...contenutiProgrammati // Tutti i contenuti programmati sempre visibili
  ]
    .filter(contenuto => {
      // Filtro cliente
      if (selectedClienteId && contenuto.clienteId !== selectedClienteId) {
        return false;
      }

      // Filtro stato
      if (selectedStato && contenuto.stato !== selectedStato) {
        return false;
      }

      // Per contenuti programmati, mostra sempre (il calendario deve mostrare tutto)
      if (contenuto.dataPubblicazione) {
        // Filtro date range solo se specificato
        if (dateFrom && new Date(contenuto.dataPubblicazione) < new Date(dateFrom)) {
          return false;
        }
        if (dateTo && new Date(contenuto.dataPubblicazione) > new Date(dateTo)) {
          return false;
        }
        
        console.log(`🔍 Contenuto programmato ${contenuto.id}: sempre visibile nel calendario`);
        return true;
      } else {
        // Per contenuti senza data di pubblicazione, mostra sempre (sono quelli da programmare)
        console.log(`🔍 Contenuto senza data ${contenuto.id}: sempre visibile`);
        return true;
      }
    })
    .sort((a, b) => {
      // Contenuti senza data vanno in cima
      if (!a.dataPubblicazione && !b.dataPubblicazione) return 0;
      if (!a.dataPubblicazione) return -1;
      if (!b.dataPubblicazione) return 1;
      return new Date(a.dataPubblicazione).getTime() - new Date(b.dataPubblicazione).getTime();
    });

  // Log finale dei contenuti filtrati
  console.log('✅ Contenuti filtrati per calendario:', {
    contenutiCalendario: contenutiCalendario.length,
    contenutiConData: contenutiCalendario.filter(c => c.dataPubblicazione).length,
    contenutiSenzaData: contenutiCalendario.filter(c => !c.dataPubblicazione).length,
    selectedMonth,
    selectedMonthName: monthNames[selectedMonth]
  });

  const handleScheduleContent = (contenuto: Contenuto) => {
    setSelectedContenuto(contenuto);
    setModalContent('schedule');
    
    // Imposta la data se già esiste, altrimenti suggerisci il prossimo giorno-calendario del cliente
    if (contenuto.dataPubblicazione) {
      const existingDate = new Date(contenuto.dataPubblicazione);
      const dateTimeLocalString = formatDateForInput(existingDate);
      setDataProgrammazione(dateTimeLocalString);
    } else {
      const cliente = getClienteById(contenuto.clienteId);
      setDataProgrammazione(suggerisciSlot(cliente, state.contenuti, tipoDi(contenuto)));
    }
    
    setShowModal(true);
  };

  const handleScheduleSubmit = async () => {
    if (!selectedContenuto || !dataProgrammazione.trim()) {
      toast.error('Seleziona una data e ora per la programmazione');
      return;
    }

    try {
      toast.loading('Programmazione in corso...');

      console.log('🚨 RAW INPUT DEBUG:', {
        dataProgrammazioneRaw: dataProgrammazione,
        dataProgrammazioneType: typeof dataProgrammazione,
        dataProgrammazioneLength: dataProgrammazione?.length,
        stringValue: JSON.stringify(dataProgrammazione),
        splitT: dataProgrammazione.split('T'),
        beforeParsing: 'About to call parseDateFromInput'
      });

      // Usa la funzione utility per parsing corretto
      let localDate = parseDateFromInput(dataProgrammazione);

      console.log('🔍 Data selezionata nel handleScheduleSubmit (PRIMA della correzione):', {
        input: dataProgrammazione,
        parsedDate: localDate,
        month: localDate.getMonth() + 1,
        monthName: localDate.toLocaleString('it', { month: 'long' }),
        isoString: localDate.toISOString(),
        localeDateString: localDate.toLocaleDateString('it-IT'),
        localeTimeString: localDate.toLocaleTimeString('it-IT')
      });

      // ✅ UNIVERSAL DATE FIX: Sempre usa il parsing controllato senza logica complessa
      // Valida che il mese parsato corrisponda a quello dell'input
      const inputMonth = parseInt(dataProgrammazione.split('-')[1]);
      const parsedMonth = localDate.getMonth() + 1;

      if (inputMonth !== parsedMonth) {
        console.log('🚨 MONTH MISMATCH DETECTED - Ricostruendo data con parsing diretto');
        const parts = dataProgrammazione.split('T');
        const datePart = parts[0]; // YYYY-MM-DD
        const timePart = parts[1]; // HH:MM

        const [year, month, day] = datePart.split('-').map(Number);
        const [hours, minutes] = timePart.split(':').map(Number);

        // Ricostruisci data in modo sicuro (month-1 perché JavaScript usa indice 0-based)
        localDate = new Date(year, month - 1, day, hours, minutes, 0, 0);

        console.log('✅ DATE FIX APPLIED:', {
          originalInput: dataProgrammazione,
          inputMonth: inputMonth,
          inputMonthName: new Date(2025, inputMonth - 1, 1).toLocaleString('it', { month: 'long' }),
          correctedMonth: localDate.getMonth() + 1,
          correctedMonthName: localDate.toLocaleString('it', { month: 'long' }),
          correctedDate: localDate
        });
      }

      console.log('🔍 Data FINALE che verrà passata a updateContenuto:', {
        finalDate: localDate,
        finalMonth: localDate.getMonth() + 1,
        finalMonthName: localDate.toLocaleString('it', { month: 'long' }),
        finalDay: localDate.getDate(),
        finalHours: localDate.getHours()
      });
      
      // Prima aggiorna lo stato
      await updateContenutoStatus(selectedContenuto.id, StatoContenuto.PROGRAMMATO, `Programmato per ${format(localDate, 'dd/MM/yyyy HH:mm')}`);
      
      // Poi aggiorna la data di pubblicazione
      await updateContenuto(selectedContenuto.id, {
        dataPubblicazione: localDate
      });
      
      toast.dismiss();
      toast.success('Contenuto programmato con successo!');
      closeModal();
      
      // Ricarica immediatamente i contenuti per aggiornare il calendario
      try {
        await loadContenuti();
        console.log('✅ Contenuti ricaricati after handleScheduleSubmit');
        
        // Secondo refresh per assicurarsi che il calendario sia aggiornato
        setTimeout(async () => {
          try {
            await loadContenuti();
            setRefreshTrigger(prev => prev + 1); // Forza refresh del memo
            console.log('✅ Contenuti ricaricati (secondo refresh handleScheduleSubmit)');
          } catch (error) {
            console.error('Errore nel secondo refresh handleScheduleSubmit:', error);
          }
        }, 500);
      } catch (error) {
        console.error('Errore nel refresh dei contenuti:', error);
      }
    } catch (error) {
      toast.dismiss();
      console.error('Errore nella programmazione:', error);
      toast.error('Errore nella programmazione del contenuto. Riprova.');
    }
  };

  const handleCancelScheduling = async (contenutoId: string) => {
    // Già in coda su Metricool (pubblica): lo dice prima di annullare
    const inCoda = state.contenuti.find((c) => c.id === contenutoId);
    const avviso = inCoda && (inCoda.uscitaStato === 'in_coda' || inCoda.uscitaStato === 'in_uscita')
      ? 'È già in coda su Metricool: pubblica lo toglierà da lì entro 2 minuti. Annullo la programmazione?'
      : 'Sei sicuro di voler annullare la programmazione di questo contenuto?';
    if (!window.confirm(avviso)) {
      return;
    }

    try {
      toast.loading('Annullamento in corso...');

      // Usa la nuova funzione per annullare la programmazione
      await cancelScheduling(contenutoId, 'Programmazione annullata');

      toast.dismiss();
      toast.success('Programmazione annullata con successo!');

      // Ricarica immediatamente i contenuti senza timeout
      try {
        await loadContenuti();
        console.log('✅ Contenuti ricaricati after handleCancelScheduling');

        // Secondo refresh per assicurarsi che il calendario sia aggiornato
        setTimeout(async () => {
          try {
            await loadContenuti();
            setRefreshTrigger(prev => prev + 1); // Forza refresh del memo
            console.log('✅ Contenuti ricaricati (secondo refresh handleCancelScheduling)');
          } catch (error) {
            console.error('Errore nel secondo refresh handleCancelScheduling:', error);
          }
        }, 500);
      } catch (error) {
        console.error('Errore nel refresh dei contenuti:', error);
      }
    } catch (error) {
      toast.dismiss();
      console.error('Errore nell\'annullamento:', error);
      toast.error('Errore nell\'annullamento della programmazione. Riprova.');
    }
  };

  // "Rimuovi": toglie il contenuto dalla coda "da programmare" e lo lascia solo
  // nel Copywriter. Non tocca "registrato" né i conteggi: rientra qui quando il
  // Videomaker preme Programma o lo segna registrato.
  const handleRemoveFromScheduling = async (contenuto: Contenuto) => {
    if (!window.confirm(`Togliere "${contenuto.titolo}" dai contenuti da programmare? Resterà nella pagina Copywriter.`)) {
      return;
    }

    try {
      await updateContenuto(contenuto.id, { rimossoDaProgrammazione: true });
      await loadContenuti();
      toast.success('Contenuto rimosso: lo trovi nella pagina Copywriter');
    } catch (error) {
      console.error('Errore nella rimozione dalla programmazione:', error);
      toast.error('Errore nella rimozione. Riprova.');
    }
  };

  const handleDeleteContenuto = async (contenutoId: string) => {
    if (!window.confirm('Sei sicuro di voler eliminare definitivamente questo contenuto? Questa azione non può essere annullata.')) {
      return;
    }

    try {
      toast.loading('Eliminazione in corso...');

      await deleteContenuto(contenutoId);

      toast.dismiss();
      toast.success('Contenuto eliminato con successo!');

      closeModal();

      // Ricarica i contenuti
      try {
        await loadContenuti();
        console.log('✅ Contenuti ricaricati after handleDeleteContenuto');

        setTimeout(async () => {
          try {
            await loadContenuti();
            setRefreshTrigger(prev => prev + 1);
            console.log('✅ Contenuti ricaricati (secondo refresh handleDeleteContenuto)');
          } catch (error) {
            console.error('Errore nel secondo refresh handleDeleteContenuto:', error);
          }
        }, 500);
      } catch (error) {
        console.error('Errore nel refresh dei contenuti:', error);
      }
    } catch (error) {
      toast.dismiss();
      console.error('Errore nell\'eliminazione:', error);
      toast.error('Errore nell\'eliminazione del contenuto. Riprova.');
    }
  };

  const handleToggleAdvUsed = async (contenuto: Contenuto) => {
    try {
      await updateContenuto(contenuto.id, { advUtilizzato: !contenuto.advUtilizzato });
    } catch (error) {
      console.error('Errore nel segnare il reel come utilizzato:', error);
    }
  };

  const handleViewDetails = (contenuto: Contenuto) => {
    setSelectedContenuto(contenuto);
    setModalContent('schedule');
    
    if (contenuto.dataPubblicazione) {
      const existingDate = new Date(contenuto.dataPubblicazione);
      const dateTimeLocalString = formatDateForInput(existingDate);
      setDataProgrammazione(dateTimeLocalString);
    } else {
      setDataProgrammazione('');
    }
    
    setShowModal(true);
  };


  // Funzione per assegnare un contenuto direttamente
  const handleDirectAssignment = async (contenuto: Contenuto, date: Date) => {
    console.log('🔍 handleDirectAssignment chiamata con:', {
      contenutoId: contenuto.id,
      contenutoTitolo: contenuto.titolo,
      inputDate: date,
      dateString: `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`,
      month: date.getMonth() + 1,
      day: date.getDate(),
      year: date.getFullYear(),
      dateFormatted: formatDateForInput(date)
    });
    
    try {
      const loadingToast = toast.loading('Assegnazione in corso...');
      
      // Crea una nuova data mantenendo la stessa data ma con ore precise
      // Usa la utility per evitare problemi di timezone
      const assignmentDate = createDateWithTime(date, 9, 0);
      
      // Valida che il mese non sia cambiato durante la creazione
      if (!validateDateMonth(date, assignmentDate)) {
        toast.dismiss();
        toast.error('Errore nella gestione della data. Riprova.');
        return;
      }
      
      console.log('🔍 Data di assegnazione corretta:', {
        originalDate: date,
        assignmentDate: assignmentDate,
        dateString: format(assignmentDate, 'dd/MM/yyyy HH:mm'),
        isoString: assignmentDate.toISOString()
      });
      
      // Prima aggiorna lo stato
      await updateContenutoStatus(contenuto.id, StatoContenuto.PROGRAMMATO, `Programmato per ${format(assignmentDate, 'dd/MM/yyyy HH:mm')}`);
      
      // Poi aggiorna la data di pubblicazione
      console.log('🔍 Chiamando updateContenuto con dataPubblicazione:', {
        contenutoId: contenuto.id,
        dataPubblicazione: assignmentDate,
        dataPubblicazioneISO: assignmentDate.toISOString(),
        dataPubblicazioneString: format(assignmentDate, 'dd/MM/yyyy HH:mm')
      });
      
      await updateContenuto(contenuto.id, {
        dataPubblicazione: assignmentDate
      });
      
      toast.dismiss(loadingToast);
      const successToast = toast.success('Contenuto assegnato con successo!');
      
      // Ricarica immediatamente i contenuti per aggiornare il calendario
      try {
        await loadContenuti();
        console.log('✅ Contenuti ricaricati dopo assegnazione diretta');
        
        // Piccolo delay per assicurarsi che il backend abbia processato la richiesta
        setTimeout(async () => {
          try {
            await loadContenuti();
            setRefreshTrigger(prev => prev + 1); // Forza refresh del memo
            console.log('✅ Contenuti ricaricati (secondo refresh)');
            toast.dismiss(successToast);
            toast.success('Calendario aggiornato!', { duration: 2000 });
          } catch (error) {
            console.error('Errore nel secondo refresh:', error);
          }
        }, 500);
      } catch (error) {
        console.error('Errore nel refresh dei contenuti dopo assegnazione:', error);
      }
    } catch (error) {
      toast.dismiss();
      toast.error('Errore durante l\'assegnazione del contenuto');
      console.error('Error assigning content:', error);
    }
  };

  // Raggruppa contenuti per data con logica timezone-safe
  const contenutiRaggrupati = useMemo(() => {
    return contenutiCalendario.reduce((acc, contenuto) => {
      if (!contenuto.dataPubblicazione) {
        if (!acc['no-date']) {
          acc['no-date'] = [];
        }
        acc['no-date'].push(contenuto);
        return acc;
      }
      
      // Usa la utility sicura per creare la chiave
      const pubDate = new Date(contenuto.dataPubblicazione);
      const dataString = createDateKey(pubDate);
      
      console.log('📅 Raggruppamento contenuto:', {
        contenutoId: contenuto.id,
        titolo: contenuto.titolo,
        dataPubblicazione: contenuto.dataPubblicazione,
        parsedDate: pubDate,
        dataString,
        year: pubDate.getFullYear(),
        month: pubDate.getMonth() + 1,
        day: pubDate.getDate()
      });
      
      if (!acc[dataString]) {
        acc[dataString] = [];
      }
      acc[dataString].push(contenuto);
      return acc;
    }, {} as Record<string, Contenuto[]>);
  }, [contenutiCalendario, refreshTrigger]);





  const clearFilters = () => {
    setPageState({
      client: '',
      from: '',
      to: '',
      stato: '',
      month: String(new Date().getMonth()),
    });
  };

  // Funzione helper per analizzare lo stato di copertura di un giorno
  const analyzeDayStatus = (date: Date, contenutiGiorno: Contenuto[], forIndex: boolean = false): { 
    shouldShow: boolean; 
    isComplete: boolean; 
    clientsPreferring: Cliente[];
    clientsWithContent: Cliente[];
    clientsMissing: Cliente[];
  } => {
    // Se non è per l'indice, mostra solo contenuti programmati senza filtri
    if (!forIndex) {
      return {
        shouldShow: contenutiGiorno.length > 0,
        isComplete: true,
        clientsPreferring: [],
        clientsWithContent: [],
        clientsMissing: []
      };
    }

    // Per l'indice, mantieni la logica completa dei giorni preferiti
    const dayNames = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];
    const dayName = dayNames[date.getDay()];
    
    // Trova tutti i clienti che preferiscono questo giorno
    const clientsPreferring = state.clienti.filter(cliente => 
      cliente.giorniPreferitiRiprese?.includes(dayName)
    );
    
    if (clientsPreferring.length === 0) {
      // Nessun cliente preferisce questo giorno
      return {
        shouldShow: false,
        isComplete: false,
        clientsPreferring: [],
        clientsWithContent: [],
        clientsMissing: []
      };
    }
    
    // Trova quali clienti hanno già contenuti programmati per questo giorno
    const clientsWithContent = contenutiGiorno.map(contenuto => 
      getClienteById(contenuto.clienteId)
    ).filter(Boolean) as Cliente[];
    
    const clientsWithContentIds = new Set(clientsWithContent.map(c => c.id));
    
    // Trova i clienti che preferiscono questo giorno ma non hanno contenuti
    const clientsMissing = clientsPreferring.filter(cliente => 
      !clientsWithContentIds.has(cliente.id)
    );
    
    // Mostra il giorno se:
    // - TUTTI i clienti che lo preferiscono hanno contenuti (completo)
    // - NESSUN cliente che lo preferisce ha contenuti (completamente vuoto)
    const isComplete = clientsMissing.length === 0;
    const isEmpty = clientsWithContent.length === 0;
    const shouldShow = isComplete || isEmpty;
    
    return {
      shouldShow,
      isComplete,
      clientsPreferring,
      clientsWithContent,
      clientsMissing
    };
  };

  // Mobile touch handlers for calendar navigation
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY
    });
    setDragStartTime(Date.now());
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY
    });
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const deltaX = touchStart.x - touchEnd.x;
    const deltaY = touchStart.y - touchEnd.y;
    const touchDuration = Date.now() - dragStartTime;
    
    // Only process if it's a quick swipe (less than 300ms) and primarily horizontal
    if (touchDuration < 300 && Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
      if (deltaX > 0) {
        // Swipe left - next month
        navigateMonth(1);
      } else {
        // Swipe right - previous month
        navigateMonth(-1);
      }
    }
    
    setTouchStart(null);
    setTouchEnd(null);
  };

  const navigateMonth = (direction: number) => {
    const newMonth = selectedMonth + direction;
    if (newMonth >= 0 && newMonth <= 11) {
      setSelectedMonth(newMonth);
      if (isMobile) {
        // Haptic feedback on mobile
        if ('vibrate' in navigator) {
          navigator.vibrate(50);
        }
      }
    }
  };

  return (
    <>
      <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <PageHeader
        title="Pubblicazione"
        description="Programma i contenuti e gestisci il calendario editoriale"
      />

      {/* Tab Navigation: su mobile scorre in orizzontale, i tab non vanno a capo */}
      <div className="grid grid-cols-3 gap-2 sm:flex">
        <button
          onClick={() => setActiveView('calendario')}
          className={`flex items-center justify-center sm:justify-start gap-1.5 sm:gap-2 px-2 sm:px-4 py-2.5 rounded-xl text-xs sm:text-sm font-medium transition-all whitespace-nowrap min-w-0 ${
            activeView === 'calendario'
              ? 'bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 shadow-md'
              : 'bg-white dark:bg-gray-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-gray-700 hover:bg-slate-50 dark:hover:bg-gray-700'
          }`}
        >
          <Calendar className="h-4 w-4 hidden sm:block" />
          Calendario
        </button>
        <button
          onClick={() => setActiveView('daProgrammare')}
          className={`flex items-center justify-center sm:justify-start gap-1.5 sm:gap-2 px-2 sm:px-4 py-2.5 rounded-xl text-xs sm:text-sm font-medium transition-all whitespace-nowrap min-w-0 ${
            activeView === 'daProgrammare'
              ? 'bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 shadow-md'
              : 'bg-white dark:bg-gray-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-gray-700 hover:bg-slate-50 dark:hover:bg-gray-700'
          }`}
        >
          <Video className="h-4 w-4 hidden sm:block" />
          <span className={`sm:hidden ${activeView === 'daProgrammare' ? 'text-[#ffffff]' : ''}`}>Da progr.</span><span className={`hidden sm:inline ${activeView === 'daProgrammare' ? 'text-[#ffffff]' : ''}`}>Da Programmare</span>
          {contenutiProntiPerProgrammazione.length > 0 && (
            <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-semibold ${
              activeView === 'daProgrammare'
                ? 'bg-white/20 text-white dark:bg-slate-900/20 dark:text-slate-900'
                : 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
            }`}>
              {contenutiProntiPerProgrammazione.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveView('campagneAdv')}
          className={`flex items-center justify-center sm:justify-start gap-1.5 sm:gap-2 px-2 sm:px-4 py-2.5 rounded-xl text-xs sm:text-sm font-medium transition-all whitespace-nowrap min-w-0 ${
            activeView === 'campagneAdv'
              ? 'bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 shadow-md'
              : 'bg-white dark:bg-gray-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-gray-700 hover:bg-slate-50 dark:hover:bg-gray-700'
          }`}
        >
          <Megaphone className="h-4 w-4 hidden sm:block" />
          <span className={`sm:hidden ${activeView === 'campagneAdv' ? 'text-[#ffffff]' : ''}`}>ADV</span><span className={`hidden sm:inline ${activeView === 'campagneAdv' ? 'text-[#ffffff]' : ''}`}>Campagne ADV</span>
          {contenutiCampagneAdv.length > 0 && (
            <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-semibold ${
              activeView === 'campagneAdv'
                ? 'bg-white/20 text-white dark:bg-slate-900/20 dark:text-slate-900'
                : 'bg-white text-slate-900'
            }`}>
              {contenutiCampagneAdv.length}
            </span>
          )}
        </button>
      </div>

      {/* === VISTA: Da Programmare === */}
      {activeView === 'daProgrammare' && (
        <div className="glass-card dark:bg-gray-800 overflow-hidden">
          <div className="p-3 sm:p-4 lg:p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <Video className="h-5 w-5 text-amber-500" />
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-slate-100">
                Contenuti da Programmare
              </h2>
              <span className="ml-auto inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400">
                {contenutiProntiPerProgrammazione.length}
              </span>
            </div>
          </div>

          {contenutiProntiPerProgrammazione.length === 0 ? (
            <div className="px-4 sm:px-6 py-8 sm:py-12 text-center">
              <div className="flex flex-col items-center space-y-4">
                <div className="p-4 bg-slate-100 dark:bg-gray-700 rounded-full">
                  <Video className="h-8 w-8 sm:h-10 sm:w-10 text-slate-400 dark:text-slate-500" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-sm sm:text-base font-semibold text-slate-900 dark:text-slate-100" style={{ letterSpacing: '-0.02em' }}>
                    Nessun contenuto da programmare
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 max-w-md mx-auto leading-relaxed">
                    I reel appariranno qui quando verranno segnati come registrati dalla pagina Riprese; i post grafici appena il testo è confermato.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-slate-200 dark:divide-gray-700">
              {contenutiProntiPerProgrammazione.map((contenuto) => {
                const cliente = getClienteById(contenuto.clienteId);
                return (
                  <div
                    key={contenuto.id}
                    className="p-3 sm:p-4 lg:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 hover:bg-slate-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    {/* Client logo + info */}
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full overflow-hidden bg-slate-100 dark:bg-gray-700 flex-shrink-0">
                        {cliente?.logoUrl ? (
                          <img
                            src={cliente.logoUrl}
                            alt={cliente.nomeAzienda}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-sm font-medium text-slate-500 dark:text-slate-400">
                            {cliente?.nomeAzienda?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm sm:text-base font-semibold text-slate-900 dark:text-slate-100 truncate" style={{ letterSpacing: '-0.02em' }}>
                          {contenuto.titolo}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 truncate">
                            {cliente?.nomeAzienda}
                          </span>
                          <AccountDestinazioneBadge contenuto={contenuto} cliente={cliente} />
                          {isPost(contenuto) ? (
                            <>
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400"
                              title={DESCRIZIONE_FORMATO[formatoDi(contenuto)]}>
                              <Image size={10} className="flex-shrink-0" />
                              {ETICHETTA_FORMATO[formatoDi(contenuto)]}
                            </span>
                            {!contenuto.copertinaUrl && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
                                title="Il post non ha ancora la grafica: va caricata dal Copywriter prima di pubblicarlo">
                                <ImageOff size={10} className="flex-shrink-0" />
                                Senza immagine
                              </span>
                            )}
                            </>
                          ) : (
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
                            contenuto.videoRegistrato
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                              : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                          }`}>
                            {contenuto.videoRegistrato ? 'Registrato' : 'Da registrare'}
                          </span>
                          )}
                          {/* Fase Editing: ha senso solo dopo la registrazione. Si può
                              programmare anche prima del montaggio: il badge avvisa. */}
                          {!haTagUscita(contenuto) && contenuto.videoRegistrato && (
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
                              contenuto.videoMontato
                                ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
                                : 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400'
                            }`}
                              title={contenuto.videoMontato ? 'Video editato' : 'Video ancora da editare (pagina Editing)'}>
                              {contenuto.videoMontato ? 'Editato' : 'Da editare'}
                            </span>
                          )}
                          <BadgePubblica contenuto={contenuto} />
                          {contenuto.campagnaAdv && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-400"
                              title="Contenuto Campagna ADV">
                              <Megaphone size={10} className="flex-shrink-0" />
                              Campagna ADV
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Platforms + client modified badge */}
                    <div className="flex flex-wrap gap-1 sm:gap-1.5 sm:justify-end">
                      {cliente?.piattaforme?.slice(0, 3).map(piattaforma => (
                        <span
                          key={piattaforma}
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-slate-100 dark:bg-gray-700 text-slate-600 dark:text-slate-300"
                        >
                          {piattaforma}
                        </span>
                      ))}
                      {(cliente?.piattaforme?.length || 0) > 3 && (
                        <span className="text-[10px] sm:text-xs text-slate-400 dark:text-slate-500 self-center">
                          +{(cliente?.piattaforme?.length || 0) - 3}
                        </span>
                      )}
                      <DriveFlagBadge contenuto={contenuto} />
      <OpusBadge contenuto={contenuto} />
                      {contenuto.clientModifiedAt && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-800"
                          title={`Modificato dal cliente il ${new Date(contenuto.clientModifiedAt).toLocaleDateString('it-IT')} alle ${new Date(contenuto.clientModifiedAt).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`}>
                          Mod. cliente
                        </span>
                      )}
                    </div>

                    {/* Schedule + remove buttons */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => handleScheduleContent(contenuto)}
                        className="flex items-center justify-center gap-2 px-4 py-2.5 sm:py-2 rounded-xl font-medium text-sm transition-all min-h-[44px] sm:min-h-0 flex-1 sm:flex-none bg-white dark:bg-gray-700 border border-slate-200 dark:border-gray-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-gray-600"
                      >
                        <Calendar className="h-4 w-4" />
                        <span>Programma</span>
                      </button>
                      <button
                        onClick={() => handleRemoveFromScheduling(contenuto)}
                        title="Toglie il contenuto da qui: resta nella pagina Copywriter"
                        className="flex items-center justify-center gap-2 px-4 py-2.5 sm:py-2 rounded-xl font-medium text-sm transition-all min-h-[44px] sm:min-h-0 flex-1 sm:flex-none bg-white dark:bg-gray-700 border border-slate-200 dark:border-gray-600 text-slate-600 dark:text-slate-300 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 hover:border-red-200 dark:hover:border-red-800"
                      >
                        <X className="h-4 w-4" />
                        <span>Rimuovi</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* === VISTA: Campagne ADV === */}
      {activeView === 'campagneAdv' && (
        <div className="glass-card dark:bg-gray-800 overflow-hidden">
          <div className="p-3 sm:p-4 lg:p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <Megaphone className="h-5 w-5 text-pink-500" />
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-slate-100">
                Campagne ADV
              </h2>
              <span className="ml-auto inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-white text-slate-900">
                {contenutiCampagneAdv.length}
              </span>
            </div>
            <p className="mt-1 text-xs sm:text-sm text-slate-500 dark:text-slate-400">
              Contenuti Campagna ADV — non sono reel da pubblicare, quindi non vanno in programmazione.
            </p>
          </div>

          {contenutiCampagneAdv.length === 0 ? (
            <div className="px-4 sm:px-6 py-8 sm:py-12 text-center">
              <div className="flex flex-col items-center space-y-4">
                <div className="p-4 bg-slate-100 dark:bg-gray-700 rounded-full">
                  <Megaphone className="h-8 w-8 sm:h-10 sm:w-10 text-slate-400 dark:text-slate-500" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-sm sm:text-base font-semibold text-slate-900 dark:text-slate-100" style={{ letterSpacing: '-0.02em' }}>
                    Nessuna campagna ADV
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 max-w-md mx-auto leading-relaxed">
                    I contenuti spuntati come "Campagna ADV" nel Copywriter appariranno qui.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-slate-200 dark:divide-gray-700">
              {contenutiCampagneAdv.map((contenuto) => {
                const cliente = getClienteById(contenuto.clienteId);
                return (
                  <div
                    key={contenuto.id}
                    className="p-3 sm:p-4 lg:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 hover:bg-slate-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full overflow-hidden bg-slate-100 dark:bg-gray-700 flex-shrink-0">
                        {cliente?.logoUrl ? (
                          <img src={cliente.logoUrl} alt={cliente.nomeAzienda} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-sm font-medium text-slate-500 dark:text-slate-400">
                            {cliente?.nomeAzienda?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm sm:text-base font-semibold text-slate-900 dark:text-slate-100 truncate flex items-center gap-1.5" style={{ letterSpacing: '-0.02em' }}>
                          {contenuto.advUtilizzato && <CheckCircle size={15} className="text-green-500 flex-shrink-0" />}
                          {contenuto.titolo}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          <span className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 truncate">
                            {cliente?.nomeAzienda}
                          </span>
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-400">
                            <Megaphone size={10} className="flex-shrink-0" />
                            Campagna ADV
                          </span>
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
                            contenuto.videoRegistrato
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                              : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                          }`}>
                            {contenuto.videoRegistrato ? 'Registrato' : 'Da registrare'}
                          </span>
                          {contenuto.advUtilizzato && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                              <CheckCircle size={10} className="flex-shrink-0" />
                              Utilizzato
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => handleToggleAdvUsed(contenuto)}
                        title={contenuto.advUtilizzato ? 'Segna come non utilizzato' : 'Segna il reel come utilizzato'}
                        className={`flex items-center justify-center gap-2 px-4 py-2.5 sm:py-2 rounded-xl font-medium text-sm transition-all min-h-[44px] sm:min-h-0 ${
                          contenuto.advUtilizzato
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-300 dark:border-green-700'
                            : 'bg-white dark:bg-gray-700 border border-slate-200 dark:border-gray-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-gray-600'
                        }`}
                      >
                        <CheckCircle className="h-4 w-4" />
                        <span>{contenuto.advUtilizzato ? 'Utilizzato' : 'Reel utilizzato'}</span>
                      </button>
                      <button
                        onClick={() => handleViewDetails(contenuto)}
                        className="flex items-center justify-center gap-2 px-4 py-2.5 sm:py-2 bg-white dark:bg-gray-700 border border-slate-200 dark:border-gray-600 text-slate-700 dark:text-slate-200 rounded-xl font-medium text-sm hover:bg-slate-50 dark:hover:bg-gray-600 transition-all min-h-[44px] sm:min-h-0"
                      >
                        <Eye className="h-4 w-4" />
                        <span>Vedi</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* === VISTA: Calendario === */}
      {activeView === 'calendario' && (
      <>
      {/* Compact Filters - Mobile Optimized */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2 sm:gap-3 mb-4">
        {/* First row on mobile: Cliente and Stato */}
        <div className="grid grid-cols-2 sm:flex gap-2 sm:gap-3">
          {/* Cliente */}
          <select
            value={selectedClienteId}
            onChange={(e) => setSelectedClienteId(e.target.value)}
            className="w-full sm:w-auto px-3 py-2.5 sm:py-2 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-xl text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:border-[#fdf751] focus:ring-2 focus:ring-[#fdf751]/20 sm:min-w-[150px]"
          >
            <option value="">Tutti i clienti</option>
            {state.clienti
              .filter(cliente => cliente.status === 'active')
              .sort((a, b) => a.nomeAzienda.localeCompare(b.nomeAzienda, 'it', { sensitivity: 'base' }))
              .map(cliente => (
                <option key={cliente.id} value={cliente.id}>{cliente.nomeAzienda}</option>
              ))}
          </select>

          {/* Stato */}
          <select
            value={selectedStato}
            onChange={(e) => setSelectedStato(e.target.value)}
            className="w-full sm:w-auto px-3 py-2.5 sm:py-2 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-xl text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:border-[#fdf751] focus:ring-2 focus:ring-[#fdf751]/20 sm:min-w-[120px]"
          >
            <option value="">Stato</option>
            <option value={StatoContenuto.MONTATO}>Montato</option>
            <option value={StatoContenuto.PROGRAMMATO}>Programmato</option>
            <option value={StatoContenuto.PUBBLICATO}>Pubblicato</option>
          </select>
        </div>

        {/* Second row on mobile: Mese and Dates */}
        <div className="grid grid-cols-3 sm:flex gap-2 sm:gap-3">
          {/* Mese */}
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
            className="w-full sm:w-auto px-3 py-2.5 sm:py-2 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-xl text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:border-[#fdf751] focus:ring-2 focus:ring-[#fdf751]/20 sm:min-w-[120px]"
          >
            {monthNames.map((month, index) => (
              <option key={index} value={index}>{isMobile ? month.substring(0, 3) : month}</option>
            ))}
          </select>

          {/* Date From */}
          <div className="relative">
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full sm:w-[130px] pl-8 pr-2 py-2.5 sm:py-2 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-xl text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:border-[#fdf751] focus:ring-2 focus:ring-[#fdf751]/20"
            />
            <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          </div>

          {/* Date To */}
          <div className="relative">
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full sm:w-[130px] pl-8 pr-2 py-2.5 sm:py-2 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-xl text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:border-[#fdf751] focus:ring-2 focus:ring-[#fdf751]/20"
            />
            <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          </div>
        </div>

        {/* Clear and count row */}
        <div className="flex items-center justify-between sm:justify-start gap-2">
          {/* Clear */}
          {(selectedClienteId || selectedStato || dateFrom || dateTo) && (
            <button
              onClick={clearFilters}
              className="p-2.5 sm:p-2 rounded-xl text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-gray-700 transition-all"
              title="Pulisci filtri"
            >
              <X className="w-4 h-4" />
            </button>
          )}

          <span className="text-sm text-slate-500 dark:text-slate-400 sm:ml-auto">
            {contenutiCalendario.length} contenuti
          </span>
        </div>
      </div>

      {/* Calendar */}
      <div className="glass-card dark:bg-gray-800 overflow-hidden">
          <div className="p-3 sm:p-4 lg:p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-slate-100 break-words">
                Calendario Editoriale
              </h2>
              <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-3">
                {/* Share button */}
                <button
                  onClick={() => {
                    if (selectedClienteId) {
                      const cliente = state.clienti.find(c => c.id === selectedClienteId);
                      if (cliente) {
                        const shareUrl = `${window.location.origin}/calendar/client/${cliente.id}`;
                        navigator.clipboard.writeText(shareUrl);
                        toast.success('Link calendario copiato negli appunti!');
                      }
                    } else {
                      toast.error('Seleziona prima un cliente per condividere il calendario');
                    }
                  }}
                  className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-2 bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/50 text-blue-700 dark:text-blue-400 rounded-lg text-xs sm:text-sm font-medium transition-colors"
                  title="Condividi calendario cliente"
                >
                  <Share2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  <span className="hidden xs:inline sm:inline">Condividi</span>
                </button>

                {/* Month/Year display */}
                <div className="text-sm sm:text-lg font-semibold text-gray-900 dark:text-slate-100">
                  {isMobile ? `${monthNames[selectedMonth].substring(0, 3)} ${new Date().getFullYear()}` : `${monthNames[selectedMonth]} ${new Date().getFullYear()}`}
                </div>
              </div>
            </div>
          </div>
          
          <div 
            className="p-2 sm:p-4 lg:p-6"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* Mobile swipe indicator */}
            {isMobile && (
              <div className="flex items-center justify-center gap-2 mb-4 text-xs text-gray-500">
                <ChevronLeft className="h-3 w-3" />
                <span>Scorri per cambiare mese</span>
                <ChevronRight className="h-3 w-3" />
              </div>
            )}
            
            {/* Simple Calendar Grid - Mobile optimized */}
            <div className="grid grid-cols-7 gap-0.5 sm:gap-1 mb-4">
              {/* Header giorni della settimana */}
              {['L', 'M', 'M', 'G', 'V', 'S', 'D'].map((day, index) => (
                <div key={index} className="text-center font-medium text-gray-600 dark:text-gray-400 p-1 sm:p-2 text-[10px] sm:text-sm">
                  <span className="sm:hidden">{day}</span>
                  <span className="hidden sm:inline">{['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'][index]}</span>
                </div>
              ))}

              {/* Calendar days */}
              {generateCalendarDays(new Date().getFullYear(), selectedMonth).map((dayInfo) => {
                const contenutiGiorno = contenutiRaggrupati[dayInfo.dayString] || [];
                const isToday = dayInfo.isToday;
                const isCurrentMonth = dayInfo.isCurrentMonth;

                return (
                  <div
                    key={dayInfo.dayString}
                    className={`
                      border rounded sm:rounded-lg p-1 sm:p-2 min-h-[60px] sm:min-h-[100px] transition-all hover:shadow-sm
                      ${isToday ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700' :
                        isCurrentMonth ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700' : 'bg-gray-50 dark:bg-gray-900/50 border-gray-100 dark:border-gray-800'}
                    `}
                    onClick={() => {
                      if (isMobile && contenutiGiorno.length > 0) {
                        openDayModal(createDateKey(dayInfo.date), dayInfo.date, contenutiGiorno);
                      }
                    }}
                  >
                    {/* Day number */}
                    <div className="text-center mb-0.5 sm:mb-2">
                      <div className={`font-semibold text-[10px] sm:text-sm ${
                        isToday ? 'text-blue-600 dark:text-blue-400' :
                        isCurrentMonth ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-600'
                      }`}>
                        {dayInfo.displayDay}
                      </div>
                    </div>

                    {/* Content items - Mobile shows client logos, desktop shows full */}
                    {/* Mobile: Show client profile images */}
                    <div className="sm:hidden flex flex-wrap justify-center gap-0.5">
                      {contenutiGiorno.slice(0, 4).map((contenuto) => {
                        const cliente = getClienteById(contenuto.clienteId);
                        return (
                          <div
                            key={contenuto.id}
                            className={`w-4 h-4 rounded-full overflow-hidden flex-shrink-0 ${
                              isPost(contenuto)
                                ? 'ring-1 ring-purple-500 bg-slate-200 dark:bg-gray-600'
                                : contenuto.videoRegistrato
                                ? 'ring-1 ring-green-500 bg-slate-200 dark:bg-gray-600'
                                : 'ring-1 ring-red-500 bg-slate-200 dark:bg-gray-600'
                            }`}
                          >
                            {cliente?.logoUrl ? (
                              <img
                                src={cliente.logoUrl}
                                alt={cliente.nomeAzienda}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-[6px] font-medium text-slate-500 dark:text-slate-400">
                                {cliente?.nomeAzienda?.charAt(0)?.toUpperCase() || '?'}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {contenutiGiorno.length > 4 && (
                        <div className="text-[8px] text-gray-500 dark:text-gray-400">+{contenutiGiorno.length - 4}</div>
                      )}
                    </div>

                    {/* Desktop: Show full content cards with client logo */}
                    <div className="hidden sm:block space-y-1">
                      {(isDayExpanded(createDateKey(dayInfo.date)) ? contenutiGiorno : contenutiGiorno.slice(0, 3)).map((contenuto) => {
                        const cliente = getClienteById(contenuto.clienteId);

                        return (
                          <div
                            key={contenuto.id}
                            className="text-xs p-1.5 rounded cursor-pointer hover:scale-105 border border-slate-200 dark:border-gray-600 bg-white dark:bg-gray-700 transition-transform shadow-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewDetails(contenuto);
                            }}
                            title={`${cliente?.nomeAzienda} - ${contenuto.titolo}`}
                          >
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <div className="w-5 h-5 rounded-full overflow-hidden bg-slate-100 dark:bg-gray-600 flex-shrink-0">
                                {cliente?.logoUrl ? (
                                  <img
                                    src={cliente.logoUrl}
                                    alt={cliente.nomeAzienda}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-[8px] font-medium text-slate-500 dark:text-slate-400">
                                    {cliente?.nomeAzienda?.charAt(0)?.toUpperCase() || '?'}
                                  </div>
                                )}
                              </div>
                              <div className="font-medium truncate text-slate-800 dark:text-slate-200 flex-1">
                                {cliente?.nomeAzienda}
                              </div>
                              {haTagUscita(contenuto) ? (
                                <BadgePubblica contenuto={contenuto} compatto />
                              ) : (<>
                              {isPost(contenuto) ? (
                                <span className="inline-flex items-center px-1 py-0.5 rounded text-[8px] font-semibold flex-shrink-0 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400" title={ETICHETTA_FORMATO[formatoDi(contenuto)]}>
                                  {isCaroselloFoto(contenuto) ? 'FOTO' : 'POST'}
                                </span>
                              ) : (
                              <span className={`inline-flex items-center px-1 py-0.5 rounded text-[8px] font-semibold flex-shrink-0 ${
                                contenuto.videoRegistrato
                                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                  : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                              }`}>
                                {contenuto.videoRegistrato ? 'REC' : 'DA REG'}
                              </span>
                              )}
                              {/* Registrato ma non montato: il video non è pronto per l'uscita */}
                              {contenuto.videoRegistrato && !contenuto.videoMontato && contenuto.stato === StatoContenuto.PROGRAMMATO && (
                                <span
                                  className="inline-flex items-center px-1 py-0.5 rounded text-[8px] font-semibold flex-shrink-0 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400"
                                  title="Video ancora da montare (pagina Editing)"
                                >
                                  DA EDIT
                                </span>
                              )}
                              <BadgePubblica contenuto={contenuto} compatto />
                              </>)}
                            </div>
                            <div className="truncate text-slate-600 dark:text-slate-400 pl-6">{contenuto.titolo}</div>
                          </div>
                        );
                      })}

                      {contenutiGiorno.length > 3 && !isDayExpanded(createDateKey(dayInfo.date)) && (
                        <button
                          className="text-xs text-blue-600 dark:text-blue-400 text-center hover:text-blue-800 dark:hover:text-blue-300 cursor-pointer w-full py-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isMobile) {
                              openDayModal(createDateKey(dayInfo.date), dayInfo.date, contenutiGiorno);
                            } else {
                              toggleDayExpansion(createDateKey(dayInfo.date));
                            }
                          }}
                        >
                          +{contenutiGiorno.length - 3} altri
                        </button>
                      )}

                      {contenutiGiorno.length > 3 && isDayExpanded(createDateKey(dayInfo.date)) && !isMobile && (
                        <button
                          className="text-xs text-gray-500 dark:text-gray-400 text-center hover:text-gray-700 dark:hover:text-gray-300 cursor-pointer w-full py-1 rounded hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleDayExpansion(createDateKey(dayInfo.date));
                          }}
                        >
                          Mostra meno
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          
        </div>
      </div>
      </>
      )}

      </div>


      {/* Modal per programmare contenuto */}
      {showModal && selectedContenuto && createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4"
          onClick={handleBackdropClick}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-[4px]" />

          {/* Modal Content — chiaro, stile Radar */}
          <div
            className="relative w-full max-w-[440px] bg-white rounded-[10px] overflow-hidden flex flex-col max-h-[90vh] border border-slate-200 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex-shrink-0 flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <h2 className="text-base sm:text-lg font-semibold text-slate-900 pr-2" style={{ letterSpacing: '-0.02em' }}>
                Programma contenuto
              </h2>
              <button
                onClick={closeModal}
                className="w-9 h-9 rounded-md flex items-center justify-center text-slate-500 bg-slate-50 border border-slate-200 hover:bg-slate-100 hover:text-slate-900 transition-all flex-shrink-0"
                aria-label="Chiudi"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div
              className="flex-1 px-4 sm:px-6 py-4 sm:py-5 overflow-y-auto space-y-4 sm:space-y-5"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              {/* Titolo contenuto + cliente */}
              {(() => {
                const cliente = getClienteById(selectedContenuto.clienteId);
                return (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-slate-50 flex-shrink-0 border border-slate-200">
                      {cliente?.logoUrl ? (
                        <img src={cliente.logoUrl} alt={cliente.nomeAzienda} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-sm font-semibold text-slate-500">
                          {cliente?.nomeAzienda?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-slate-900 truncate">{selectedContenuto.titolo}</p>
                      <p className="text-xs text-slate-500 truncate">{cliente?.nomeAzienda}</p>
                      {/* Lavorazione: chi ha già toccato il contenuto (videomaker → Registrato,
                          video editor → Editato). Grigio = passaggio ancora da fare. */}
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        <AccountDestinazioneBadge contenuto={selectedContenuto} cliente={cliente} />
                        {isPost(selectedContenuto) ? (
                          <>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-100 text-purple-700 border border-purple-200"
                            title={DESCRIZIONE_FORMATO[formatoDi(selectedContenuto)]}>
                            <Image size={10} className="flex-shrink-0" />
                            {ETICHETTA_FORMATO[formatoDi(selectedContenuto)]}
                          </span>
                          {!selectedContenuto.copertinaUrl && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-700 border border-red-200"
                              title="Il post non ha ancora la grafica: va caricata dal Copywriter prima di pubblicarlo">
                              <ImageOff size={10} className="flex-shrink-0" />
                              Senza immagine
                            </span>
                          )}
                          </>
                        ) : (
                        <>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                          selectedContenuto.videoRegistrato
                            ? 'bg-blue-100 text-blue-700 border border-blue-200'
                            : 'bg-slate-100 text-slate-500 border border-slate-200'
                        }`} title={selectedContenuto.videoRegistrato ? 'Il videomaker ha segnato il video come registrato' : 'Il videomaker non ha ancora registrato il video'}>
                          <Video size={10} className="flex-shrink-0" />
                          {selectedContenuto.videoRegistrato ? 'Registrato' : 'Da registrare'}
                        </span>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                          selectedContenuto.videoMontato
                            ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                            : 'bg-slate-100 text-slate-500 border border-slate-200'
                        }`} title={selectedContenuto.videoMontato ? 'Il video editor ha segnato il video come montato' : 'Il video editor non ha ancora montato il video (pagina Editing)'}>
                          <CheckCircle size={10} className="flex-shrink-0" />
                          {selectedContenuto.videoMontato ? 'Editato' : 'Da editare'}
                        </span>
                        </>
                        )}
                        <BadgePubblica contenuto={selectedContenuto} />
                        <a
                          href={`https://pubblica.bymarke.com/contenuti/${selectedContenuto.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[10px] font-medium text-slate-500 underline hover:text-slate-900"
                          title="Conferma del cliente e uscita su Metricool, in pubblica"
                        >
                          Apri in pubblica
                        </a>
                      </div>
                    </div>
                    {selectedContenuto.campagnaAdv && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-pink-500/15 text-pink-300 border border-pink-500/30 flex-shrink-0">
                        <Megaphone size={11} className="flex-shrink-0" /> ADV
                      </span>
                    )}
                  </div>
                );
              })()}

              {/* Data programmazione (non per le Campagne ADV) */}
              {selectedContenuto.campagnaAdv ? (
                <div className="bg-pink-500/10 p-3 sm:p-4 rounded-md border border-pink-500/30 flex items-center gap-2">
                  <Megaphone className="h-4 w-4 text-pink-400 flex-shrink-0" />
                  <p className="text-sm text-pink-300">
                    Campagna ADV — non va programmata come i reel classici.
                  </p>
                </div>
              ) : (() => {
                const [datePart = '', timePart = ''] = (dataProgrammazione || '').split('T');
                const clienteModal = getClienteById(selectedContenuto.clienteId);
                const orarioCliente = clienteModal?.orarioPubblicazione || '21:00';
                const giorniCliente = clienteModal ? giorniUscita(clienteModal) : [];
                // Feed a scacchiera: nel giorno scelto tocca a un reel o a un post?
                const alterna = clienteModal ? alternaReelEPost(clienteModal) : false;
                const tipoAtteso = alterna && clienteModal && datePart
                  ? tipoAttesoInData(clienteModal, new Date(`${datePart}T12:00:00`))
                  : null;
                const tipoQuesto = tipoDi(selectedContenuto);
                const oggi = (() => {
                  const d = new Date();
                  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                })();
                const orari = Array.from(new Set([orarioCliente, '09:00', '12:00', '15:00', '18:00', '21:00'])).slice(0, 6);
                return (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-2">Giorno</label>
                      <input
                        type="date"
                        value={datePart}
                        onChange={(e) => setDataProgrammazione(e.target.value ? `${e.target.value}T${timePart || orarioCliente}` : '')}
                        className="w-full px-3 py-3 border border-slate-200 rounded-md bg-slate-50 text-slate-900 focus:outline-none focus:border-slate-900 transition-all text-base"
                      />
                      {giorniCliente.length > 0 && (
                        <p className="mt-1.5 text-[11px] text-slate-400">
                          Giorni di pubblicazione di {clienteModal?.nomeAzienda}:{' '}
                          <span className="text-[#fdf751] font-bold">{giorniCliente.join(', ')}</span>
                          {alterna ? ` (reel e ${PLURALE_POST[etichettaPostCliente(clienteModal)] ?? 'post'} a rotazione): suggerito il prossimo libero che tocca a questo tipo.` : ': suggerito il prossimo libero.'}
                        </p>
                      )}
                      {tipoAtteso && (
                        <p className={`mt-1 text-[11px] font-semibold ${tipoAtteso === tipoQuesto ? 'text-emerald-600' : 'text-amber-600'}`}>
                          {tipoAtteso === tipoQuesto
                            ? `In questo giorno tocca a un ${(tipoAtteso === 'post' ? etichettaPostCliente(clienteModal) : ETICHETTA_TIPO[tipoAtteso]).toLowerCase()}: l'alternanza del feed è rispettata.`
                            : `In questo giorno toccherebbe a un ${(tipoAtteso === 'post' ? etichettaPostCliente(clienteModal) : ETICHETTA_TIPO[tipoAtteso]).toLowerCase()}: così si rompe la rotazione del feed.`}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-2">Orario</label>
                      <div className="grid grid-cols-3 gap-1.5 sm:gap-2">
                        {orari.map((t) => (
                          <button
                            key={t}
                            type="button"
                            onClick={() => setDataProgrammazione(`${datePart || oggi}T${t}`)}
                            className={`py-2 rounded-md text-xs sm:text-sm font-semibold border transition-all min-h-[40px] ${
                              timePart === t
                                ? 'bg-[#fdf751] !text-black border-[#fdf751]'
                                : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                            }`}
                          >
                            {t}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {selectedContenuto.dataPubblicazione && (
                <div className="bg-blue-500/10 p-3 sm:p-4 rounded-md border border-blue-500/30">
                  <p className="text-xs font-medium text-slate-500 mb-1">Attualmente programmato per</p>
                  <p className="text-blue-300 text-sm">
                    {format(new Date(selectedContenuto.dataPubblicazione), 'dd MMMM yyyy \'alle\' HH:mm', { locale: it })}
                  </p>
                </div>
              )}
            </div>
              
            {/* Footer — CTA del momento */}
            <div className="flex-shrink-0 px-5 py-4 border-t border-slate-100 flex flex-col gap-2">
              {/* MONTATO / TESTO_PRONTO / REGISTRATO pronto (mai per ADV): stessa regola della lista «da programmare» */}
              {(selectedContenuto.stato === StatoContenuto.MONTATO ||
                selectedContenuto.stato === StatoContenuto.TESTO_PRONTO ||
                (selectedContenuto.stato === StatoContenuto.REGISTRATO && prontoPerProgrammazione(selectedContenuto))) &&
                !selectedContenuto.campagnaAdv && (
                <button
                  onClick={handleScheduleSubmit}
                  disabled={!dataProgrammazione || isLoading}
                  className="w-full flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-b from-[#fff7a8] to-[#fdf751] text-slate-900 rounded-md font-semibold text-sm sm:text-base shadow-[0_4px_14px_rgba(253,247,81,0.25)] hover:-translate-y-0.5 disabled:opacity-50 disabled:translate-y-0 transition-all min-h-[44px]"
                >
                  <Calendar className="h-4 w-4" />
                  <span>Programma</span>
                </button>
              )}

              {/* PROGRAMMATO */}
              {selectedContenuto.stato === StatoContenuto.PROGRAMMATO && (
                <>
                  <button
                    onClick={handleScheduleSubmit}
                    disabled={!dataProgrammazione || isLoading}
                    className="w-full flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-b from-[#fff7a8] to-[#fdf751] text-slate-900 rounded-md font-semibold text-sm sm:text-base shadow-[0_4px_14px_rgba(253,247,81,0.25)] hover:-translate-y-0.5 disabled:opacity-50 disabled:translate-y-0 transition-all min-h-[44px]"
                  >
                    <Calendar className="h-4 w-4" />
                    <span>Aggiorna programmazione</span>
                  </button>
                  <button
                    onClick={() => { handleCancelScheduling(selectedContenuto.id); closeModal(); }}
                    disabled={isLoading}
                    className="w-full flex items-center justify-center gap-2 px-5 py-2.5 bg-red-500/10 text-red-400 border border-red-500/30 rounded-md font-medium text-sm hover:bg-red-500/20 disabled:opacity-50 transition-all min-h-[44px]"
                  >
                    <X className="h-4 w-4" />
                    <span>Annulla programmazione</span>
                  </button>
                </>
              )}

              {/* PUBBLICATO */}
              {selectedContenuto.stato === StatoContenuto.PUBBLICATO && (
                <>
                  <button
                    onClick={handleScheduleSubmit}
                    disabled={!dataProgrammazione || isLoading}
                    className="w-full flex items-center justify-center gap-2 px-5 py-2.5 bg-emerald-500 text-white rounded-md font-semibold text-sm sm:text-base hover:bg-emerald-600 disabled:opacity-50 transition-all min-h-[44px]"
                  >
                    <Calendar className="h-4 w-4" />
                    <span>Aggiorna data pubblicazione</span>
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm('Sei sicuro di voler archiviare questo contenuto pubblicato?')) {
                        handleCancelScheduling(selectedContenuto.id);
                        closeModal();
                      }
                    }}
                    disabled={isLoading}
                    className="w-full flex items-center justify-center gap-2 px-5 py-2.5 bg-amber-500/10 text-amber-400 border border-amber-500/30 rounded-md font-medium text-sm hover:bg-amber-500/20 disabled:opacity-50 transition-all min-h-[44px]"
                  >
                    <X className="h-4 w-4" />
                    <span>Archivia contenuto</span>
                  </button>
                </>
              )}

              {/* ARCHIVIATO */}
              {selectedContenuto.stato === StatoContenuto.ARCHIVIATO && (
                <button
                  onClick={handleScheduleSubmit}
                  disabled={!dataProgrammazione || isLoading}
                  className="w-full flex items-center justify-center gap-2 px-5 py-2.5 bg-slate-100 text-slate-900 border border-slate-200 rounded-md font-medium text-sm sm:text-base hover:bg-slate-200 disabled:opacity-50 transition-all min-h-[44px]"
                >
                  <Calendar className="h-4 w-4" />
                  <span>Riprogramma</span>
                </button>
              )}

              {/* BOZZA / REGISTRATO */}
              {(selectedContenuto.stato === StatoContenuto.BOZZA ||
                (selectedContenuto.stato === StatoContenuto.REGISTRATO && !prontoPerProgrammazione(selectedContenuto))) && (
                <div className="w-full flex items-center justify-center gap-2 px-5 py-2.5 bg-slate-50 text-slate-400 rounded-md text-sm sm:text-base min-h-[44px]">
                  <Clock className="h-4 w-4" />
                  <span>Non pronto per programmazione</span>
                </div>
              )}

              {/* Chiudi */}
              <button
                onClick={closeModal}
                className="w-full flex items-center justify-center gap-2 px-5 py-2.5 bg-white text-slate-600 border border-slate-200 rounded-md font-medium text-sm sm:text-base hover:bg-slate-50 transition-all min-h-[44px]"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal per visualizzare contenuti del giorno su mobile */}
      {dayModal.isOpen && createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          onClick={closeDayModal}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/40 backdrop-blur-[4px]" />

          {/* Modal Content */}
          <div
            className="relative w-full max-w-[520px] bg-white rounded-[10px] overflow-hidden flex flex-col max-h-[85vh]"
            style={{
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
              border: '1px solid rgba(0, 0, 0, 0.08)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex-shrink-0 flex items-center justify-between px-6 py-5 border-b border-slate-200/80">
              <h2 className="text-lg font-semibold text-slate-900" style={{ letterSpacing: '-0.02em' }}>
                Contenuti del {dayModal.date}
              </h2>
              <button
                onClick={closeDayModal}
                className="w-9 h-9 rounded-xl flex items-center justify-center text-slate-400 bg-slate-100/80 border border-slate-200/80 hover:bg-slate-200/80 hover:text-slate-600 transition-all"
                aria-label="Chiudi"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div
              className="flex-1 overflow-y-auto"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              {dayModal.contenuti.map((contenuto, index) => {
                const cliente = getClienteById(contenuto.clienteId);
                return (
                  <div
                    key={contenuto.id}
                    className={`p-5 ${index !== dayModal.contenuti.length - 1 ? 'border-b border-slate-100' : ''}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full overflow-hidden bg-slate-100 flex-shrink-0">
                        {cliente?.logoUrl ? (
                          <img
                            src={cliente.logoUrl}
                            alt={cliente.nomeAzienda}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-sm font-medium text-slate-500">
                            {cliente?.nomeAzienda?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className="text-sm font-medium text-slate-900">
                            {cliente?.nomeAzienda}
                          </span>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            contenuto.stato === StatoContenuto.PROGRAMMATO ? 'bg-amber-50 text-amber-700' :
                            contenuto.stato === StatoContenuto.PUBBLICATO ? 'bg-green-50 text-green-700' :
                            contenuto.stato === StatoContenuto.ARCHIVIATO ? 'bg-slate-100 text-slate-600' : 'bg-blue-50 text-blue-700'
                          }`}>
                            {contenuto.stato === StatoContenuto.PROGRAMMATO ? 'Programmato' :
                             contenuto.stato === StatoContenuto.PUBBLICATO ? 'Pubblicato' :
                             contenuto.stato === StatoContenuto.ARCHIVIATO ? 'Archiviato' : contenuto.stato}
                          </span>
                          {isPost(contenuto) ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-50 text-purple-600 border border-purple-200">
                              <Image size={10} className="flex-shrink-0" />
                              {ETICHETTA_FORMATO[formatoDi(contenuto)]}
                            </span>
                          ) : (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                            contenuto.videoRegistrato
                              ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 border border-green-200 dark:border-green-800'
                              : 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800'
                          }`}>
                            {contenuto.videoRegistrato ? 'Registrato' : 'Da registrare'}
                          </span>
                          )}
                          {!haTagUscita(contenuto) && contenuto.videoRegistrato && contenuto.stato === StatoContenuto.PROGRAMMATO && (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                              contenuto.videoMontato
                                ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800'
                                : 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 border border-orange-200 dark:border-orange-800'
                            }`}>
                              {contenuto.videoMontato ? 'Editato' : 'Da editare'}
                            </span>
                          )}
                          <BadgePubblica contenuto={contenuto} />
                          {contenuto.campagnaAdv && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-pink-50 dark:bg-pink-900/20 text-pink-600 dark:text-pink-400 border border-pink-200 dark:border-pink-800"
                              title="Contenuto Campagna ADV">
                              <Megaphone size={10} className="flex-shrink-0" />
                              Campagna ADV
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-600 line-clamp-2 mb-3">
                          {contenuto.titolo}
                        </p>
                        <div className="flex gap-2 flex-wrap">
                          <button
                            onClick={() => {
                              handleViewDetails(contenuto);
                              closeDayModal();
                              if ('vibrate' in navigator) {
                                navigator.vibrate(30);
                              }
                            }}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                          >
                            <Eye className="h-3 w-3" />
                            Dettagli
                          </button>
                          {contenuto.stato === StatoContenuto.MONTATO && (
                            <button
                              onClick={() => {
                                handleScheduleContent(contenuto);
                                closeDayModal();
                                if ('vibrate' in navigator) {
                                  navigator.vibrate(50);
                                }
                              }}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
                            >
                              <Share2 className="h-3 w-3" />
                              Programma
                            </button>
                          )}
                          <button
                            onClick={() => {
                              closeDayModal();
                              handleDeleteContenuto(contenuto.id);
                              if ('vibrate' in navigator) {
                                navigator.vibrate(50);
                              }
                            }}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                          >
                            <Trash2 className="h-3 w-3" />
                            Elimina
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default Programmazione; 